import { Link } from 'react-router-dom';
import { ArrowRight, BarChart2 } from 'lucide-react';
import { LogoCloud } from '@/components/ui/logo-cloud-3';

export function HeroSection() {
  return (
    <section
      className="relative w-full min-h-[90vh] flex flex-col lg:flex-row overflow-hidden"
      data-testid="home-hero"
    >
      {/* ── LEFT: Content Canvas ──────────────────────────────────── */}
      <div className="w-full lg:w-1/2 px-8 sm:px-12 lg:px-20 py-20 lg:py-[120px] flex flex-col justify-center z-10 bg-white">

        {/* Badge */}
        <div
          className="hero-fade-up inline-flex items-center gap-2 bg-slate-50 border border-slate-200 px-3 py-2 rounded w-fit mb-8 shadow-sm"
          style={{ animationDelay: '80ms' }}
          data-testid="hero-pill"
        >
          <span className="w-2 h-2 rounded-full bg-brand-orange animate-pulse flex-shrink-0" />
          <span className="font-mono text-[11px] font-medium text-brand-navy uppercase tracking-widest">
            AI Powered Supply Chain Transformation Initiative
          </span>
        </div>

        {/* Headline */}
        <h1
          className="hero-fade-up font-heading font-bold text-4xl sm:text-5xl lg:text-[52px] text-brand-navy leading-[1.1] tracking-tight mb-6 max-w-2xl"
          style={{ animationDelay: '150ms' }}
        >
          Transforming Procurement{' '}
          from{' '}
          <span className="text-brand-orange">Cost Centre</span> to{' '}
          <span className="text-brand-orange">Strategic Powerhouse</span>
        </h1>

        {/* Subhead */}
        <p
          className="hero-fade-up text-lg text-slate-500 leading-relaxed mb-10 max-w-xl"
          style={{ animationDelay: '220ms' }}
        >
          We design, build, and operate AI-powered supply chain transformation
          programs that drive efficiency, savings, agility, and compliance,
          enabling enterprises to create competitive advantage through smarter
          decision-making and optimized supply chain performance.
        </p>

        {/* CTAs */}
        <div
          className="hero-fade-up flex flex-row flex-wrap items-center gap-3 mb-12"
          style={{ animationDelay: '300ms' }}
        >
          <a
            href="https://wa.me/919637625311"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center justify-center gap-2 bg-brand-orange text-white px-7 h-11 rounded text-sm font-semibold shadow-[0_4px_20px_rgba(229,116,26,0.3)] hover:opacity-90 transition-opacity"
            data-testid="hero-cta-consultation"
          >
            Talk to us
            <ArrowRight className="size-4" />
          </a>
          <Link
            to="/case-studies"
            className="inline-flex items-center justify-center gap-2 border border-brand-navy text-brand-navy bg-transparent px-7 h-11 rounded text-sm font-semibold hover:bg-slate-50 transition-colors"
            data-testid="hero-cta-case-studies"
          >
            View Case Studies
          </Link>
        </div>

        {/* Stats cards */}
        <div
          className="hero-fade-up grid grid-cols-2 gap-4 max-w-sm"
          style={{ animationDelay: '380ms' }}
        >
          <div className="border border-slate-200 rounded p-4 bg-slate-50">
            <div className="font-mono text-[10px] text-slate-400 uppercase tracking-widest mb-1">
              Cost Reduction
            </div>
            <div className="font-heading font-bold text-2xl text-brand-navy">50–70%</div>
          </div>
          <div className="border border-slate-200 rounded p-4 bg-slate-50">
            <div className="font-mono text-[10px] text-slate-400 uppercase tracking-widest mb-1">
              Less Manual Work
            </div>
            <div className="font-heading font-bold text-2xl text-brand-navy">60%</div>
          </div>
        </div>
      </div>

      {/* ── RIGHT: Visual Canvas ──────────────────────────────────── */}
      <div className="w-full lg:w-1/2 relative min-h-[500px] lg:min-h-full bg-brand-navy overflow-hidden flex items-center justify-center p-10 lg:p-14">

        {/* Dot grid overlay */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-[0.07]"
          style={{
            backgroundImage: 'radial-gradient(circle, #ffffff 1px, transparent 1px)',
            backgroundSize: '22px 22px',
          }}
        />

        {/* Soft radial glow */}
        <div
          aria-hidden="true"
          className="absolute inset-0 opacity-20"
          style={{
            background:
              'radial-gradient(ellipse 60% 60% at 60% 50%, rgba(229,116,26,0.25) 0%, transparent 70%)',
          }}
        />

        {/* Image container */}
        <div
          className="hero-fade-up relative w-full max-w-lg aspect-[4/5] rounded-lg overflow-hidden border border-white/10 shadow-2xl group z-10"
          style={{ animationDelay: '200ms' }}
          data-testid="hero-image-container"
        >
          <img
            src="/images/hero-logistics.jpg"
            alt="Smart supply chain and port logistics powered by iMANE"
            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          />

          {/* Glassmorphism insight card */}
          <div className="absolute bottom-4 left-4 right-4 bg-white/10 backdrop-blur-md border border-white/20 rounded p-3 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-brand-orange/20 flex items-center justify-center flex-shrink-0">
              <BarChart2 className="w-5 h-5 text-brand-orange" />
            </div>
            <div>
              <div className="font-mono text-[10px] text-white/60 uppercase tracking-widest mb-0.5">
                Live Analytics
              </div>
              <div className="text-sm text-white font-semibold leading-snug">
                Procurement savings tracked. ROI verified across 15+ countries.
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

const logos = [
  { alt: 'Strategic Sourcing & Procurement', label: 'Strategic Sourcing & Procurement' },
  { alt: 'Sourcing', label: 'Sourcing' },
  { alt: 'Procure-to-Pay (P2P)', label: 'Procure-to-Pay (P2P)' },
  { alt: 'Contract Lifecycle Management (CLM)', label: 'Contract Lifecycle Management (CLM)' },
  { alt: 'Supplier Lifecycle & Risk Management', label: 'Supplier Lifecycle & Risk Management' },
  { alt: 'Invoice Automation', label: 'Invoice Automation' },
  { alt: 'Spend Analytics', label: 'Spend Analytics' },
  { alt: 'Business Networks', label: 'Business Networks' },
  { alt: 'Document Intelligence / AI Automation', label: 'Document Intelligence / AI Automation' },
  { alt: 'ERP & Finance Integrations', label: 'ERP & Finance Integrations' },
  { alt: 'Cloud & Workflow Platforms', label: 'Cloud & Workflow Platforms' },
];

export function LogosSection() {
  return (
    <section className="relative space-y-4 border-t border-brand-border pt-8 pb-12 bg-white" data-testid="hero-logos">
      <p className="text-center font-medium text-sm text-slate-500 tracking-tight md:text-base max-w-3xl mx-auto px-6 leading-relaxed">
        Trusted across leading procurement, sourcing, ERP, and AI ecosystems.
        Helping enterprises modernize spend management, supplier collaboration,
        and intelligent supply chain operations.
      </p>
      <div className="relative z-10 mx-auto max-w-4xl">
        <LogoCloud logos={logos} />
      </div>
    </section>
  );
}
